import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Bookmark, Check, Loader2 } from "lucide-react";
import { useUser } from "../hooks/useUser";
import { showSuccessToast, showErrorToast } from "../utils/toast";

export default function SaveToFavoritesModal({
  isOpen,
  onClose,
  torrent,
  onFavoriteChange,
  onDataChange,
}) {
  const { user } = useUser();
  const [isFavorite, setIsFavorite] = useState(false);
  const [compilations, setCompilations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [savingCompilation, setSavingCompilation] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCompilationName, setNewCompilationName] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Загружаем данные при открытии
  useEffect(() => {
    if (isOpen && user?.telegram_id && torrent?.id) {
      fetchData();
    }
  }, [isOpen, user?.telegram_id, torrent?.id]);

  const buildPayloadTorrent = () => ({
    id: torrent.id,
    title: torrent.title,
    category: torrent.category || torrent.badge,
    size: torrent.size,
    time: torrent.time || torrent.upload_date || torrent.scraped_at,
    image_url: torrent.image_url || torrent.image,
    image_urls: torrent.image_urls || [],
    is_exclusive: torrent.is_exclusive ?? torrent.isExclusive,
    description: torrent.description || "",
    torrent_path: torrent.torrent_path,
    archive_message_ids: torrent.archive_message_ids || [],
  });

  // Фокус на input при создании новой подборки
  useEffect(() => {
    if (isCreatingNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingNew]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
      };

      const [favCheckRes, compRes, collectionsRes] = await Promise.all([
        fetch(
          `/api/users/profile/${user.telegram_id}/favorites/${torrent.id}/check`,
          {
            headers,
          },
        ),
        fetch(`/api/users/profile/${user.telegram_id}/compilations?all=true`, {
          headers,
        }),
        fetch(
          `/api/users/profile/${user.telegram_id}/favorites/${torrent.id}/collections`,
          { headers },
        ),
      ]);

      if (favCheckRes.ok) {
        const favData = await favCheckRes.json();
        setIsFavorite(favData.is_favorite);
      }

      const collectionIds = new Set();
      if (collectionsRes.ok) {
        const collectionsData = await collectionsRes.json();
        (collectionsData.collections || []).forEach((collection) => {
          collectionIds.add(String(collection.id));
        });
      }

      if (compRes.ok) {
        const compData = await compRes.json();
        const compilationsWithCheck = (compData.compilations || []).map(
          (comp) => ({
            ...comp,
            has_torrent: collectionIds.has(String(comp.id)),
          }),
        );
        setCompilations(compilationsWithCheck);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Добавить/удалить из избранного
  const handleToggleFavorite = async () => {
    if (savingFavorite) return;
    setSavingFavorite(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
      };

      if (isFavorite) {
        const affectedCompilationIds = compilations
          .filter((comp) => comp.has_torrent)
          .map((comp) => String(comp.id));
        const deletedCompilationIds = compilations
          .filter(
            (comp) =>
              comp.has_torrent && parseInt(comp.items_count || 0, 10) <= 1,
          )
          .map((comp) => String(comp.id));

        // Проверяем, есть ли торрент в коллекциях
        const checkRes = await fetch(
          `/api/users/profile/${user.telegram_id}/favorites/${torrent.id}/collections`,
          { headers },
        );

        if (checkRes.ok) {
          const checkData = await checkRes.json();
          if (checkData.in_collections) {
            const confirmed = window.confirm(
              `This torrent is in ${checkData.collections.length} collection(s). Removing from favorites will also remove it from all collections. Continue?`,
            );
            if (!confirmed) {
              setSavingFavorite(false);
              return;
            }
          }
        }

        // Удаляем из избранного
        const res = await fetch(
          `/api/users/profile/${user.telegram_id}/favorites/${torrent.id}`,
          { method: "DELETE", headers },
        );
        if (res.ok) {
          setIsFavorite(false);
          setCompilations((prev) =>
            prev
              .map((comp) => ({
                ...comp,
                has_torrent: false,
                items_count: comp.has_torrent
                  ? Math.max(0, parseInt(comp.items_count || 0, 10) - 1)
                  : parseInt(comp.items_count || 0, 10),
              }))
              .filter((comp) => parseInt(comp.items_count || 0, 10) > 0),
          );
          onFavoriteChange?.(torrent.id, false);
          onDataChange?.({
            type: "favorite-removed",
            torrentId: torrent.id,
            affectedCompilationIds,
            deletedCompilationIds,
          });
          showSuccessToast("Removed from favorites");
        }
      } else {
        const prependToFavorites = !compilations.some(
          (comp) => comp.has_torrent,
        );

        // Добавляем в избранное
        const res = await fetch(
          `/api/users/profile/${user.telegram_id}/favorites`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ torrent_id: torrent.id }),
          },
        );
        if (res.ok) {
          setIsFavorite(true);
          onFavoriteChange?.(torrent.id, true);
          onDataChange?.({
            type: "favorite-added",
            torrent: buildPayloadTorrent(),
            prependToFavorites,
          });
          showSuccessToast("Added to favorites");
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      showErrorToast("Error saving");
    } finally {
      setSavingFavorite(false);
    }
  };

  // Добавить или удалить из компиляции
  const handleToggleCompilation = async (compilation) => {
    if (savingCompilation) return;
    const isAdding = !compilation.has_torrent;

    // Нельзя добавить в коллекцию если не в избранном
    if (isAdding && !isFavorite) {
      showErrorToast("Add to favorites first");
      return;
    }

    setSavingCompilation(compilation.id);

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
      };

      let res;
      if (isAdding) {
        res = await fetch(
          `/api/users/profile/${user.telegram_id}/compilations/${compilation.id}/items`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ torrent_id: torrent.id }),
          },
        );
      } else {
        // Удаление
        res = await fetch(
          `/api/users/profile/${user.telegram_id}/compilations/${compilation.id}/items/${torrent.id}`,
          {
            method: "DELETE",
            headers,
          },
        );
      }

      if (res.ok) {
        const data = await res.json();
        // Обновляем локальное состояние
        setCompilations((prev) => {
          if (!isAdding && data.compilation_deleted) {
            return prev.filter((c) => c.id !== compilation.id);
          }

          return prev.map((c) =>
            c.id === compilation.id
              ? {
                  ...c,
                  has_torrent: isAdding,
                  items_count:
                    parseInt(c.items_count || 0, 10) + (isAdding ? 1 : -1),
                }
              : c,
          );
        });
        showSuccessToast(
          isAdding ? "Added to collection" : "Removed from collection",
        );
        onDataChange?.({
          type: isAdding
            ? "compilation-item-added"
            : "compilation-item-removed",
          compilationId: String(compilation.id),
          torrentId: torrent.id,
          compilationDeleted: Boolean(data.compilation_deleted),
        });
      }
    } catch (error) {
      console.error("Error toggling compilation:", error);
      showErrorToast("Error updating collection");
    } finally {
      setSavingCompilation(null);
    }
  };

  // Создать новую компиляцию
  const handleCreateCompilation = async () => {
    if (!newCompilationName.trim() || creatingNew) return;
    setCreatingNew(true);

    try {
      const res = await fetch(
        `/api/users/profile/${user.telegram_id}/compilations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
          },
          body: JSON.stringify({
            name: newCompilationName.trim(),
            torrent_id: torrent.id,
          }),
        },
      );

      if (res.ok) {
        const data = await res.json();
        const createdCompilation = {
          ...data.compilation,
          items_count: 1,
          preview_items: [
            {
              id: torrent.id,
              title: torrent.title,
              image_url: torrent.image_url || torrent.image,
            },
          ],
        };
        // Добавляем новую компиляцию в список
        setCompilations((prev) => [
          {
            ...createdCompilation,
            has_torrent: true,
          },
          ...prev,
        ]);
        setNewCompilationName("");
        setIsCreatingNew(false);
        showSuccessToast("Collection created");
        onDataChange?.({
          type: "compilation-created",
          compilationId: String(data.compilation.id),
          compilation: createdCompilation,
        });
      }
    } catch (error) {
      console.error("Error creating compilation:", error);
      showErrorToast("Error creating");
    } finally {
      setCreatingNew(false);
    }
  };

  // Закрытие по клику вне модала
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            className="relative w-full max-w-md rounded-t-3xl overflow-hidden"
            style={{
              background:
                "linear-gradient(to bottom, #1a1a1f 0%, #141418 100%)",
              maxHeight: "70vh",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.2 }}
            onDragEnd={(e, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                onClose();
              }
            }}
          >
            {/* Handle bar - Drag handle */}
            <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
              <div className="w-10 h-1 rounded-full bg-gray-600" />
            </div>

            {/* Content */}
            <div
              className="px-4 pb-8 overflow-y-auto"
              style={{ maxHeight: "calc(70vh - 60px)" }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {/* Сохранено (избранное) */}
                  <div
                    className="flex items-center gap-4 p-3 rounded-2xl mb-6 cursor-pointer transition-all hover:bg-white/5"
                    onClick={handleToggleFavorite}
                    style={{
                      background: isFavorite
                        ? "rgba(99, 102, 241, 0.1)"
                        : "transparent",
                      border: isFavorite
                        ? "1px solid rgba(99, 102, 241, 0.3)"
                        : "1px solid transparent",
                    }}
                  >
                    {/* Preview image */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-800">
                      {torrent?.image_url && (
                        <img
                          src={torrent.image_url}
                          alt={torrent.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-base">
                        Saved
                      </h3>
                      <p className="text-gray-400 text-sm">Private</p>
                    </div>

                    {/* Bookmark icon */}
                    <div className="flex-shrink-0">
                      {savingFavorite ? (
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                      ) : (
                        <Bookmark
                          className={`w-6 h-6 transition-colors ${
                            isFavorite
                              ? "fill-indigo-500 text-indigo-500"
                              : "text-gray-400"
                          }`}
                        />
                      )}
                    </div>
                  </div>

                  {/* Подборки */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold text-lg">
                      Collections
                    </h3>
                    <button
                      onClick={() => setIsCreatingNew(true)}
                      className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                    >
                      New collection
                    </button>
                  </div>

                  {/* Форма создания новой подборки */}
                  <AnimatePresence>
                    {isCreatingNew && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-4 overflow-hidden"
                      >
                        <div className="p-4 rounded-2xl bg-white/5 border border-gray-700">
                          {/* Preview */}
                          <div className="flex justify-center mb-4">
                            <div className="w-32 h-24 rounded-xl overflow-hidden bg-gray-800">
                              {torrent?.image_url && (
                                <img
                                  src={torrent.image_url}
                                  alt={torrent.title}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                          </div>

                          {/* Input */}
                          <input
                            ref={inputRef}
                            type="text"
                            value={newCompilationName}
                            onChange={(e) =>
                              setNewCompilationName(e.target.value)
                            }
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleCreateCompilation()
                            }
                            placeholder="Collection name"
                            className="w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
                          />

                          {/* Buttons */}
                          <div className="flex gap-3 mt-4">
                            <button
                              onClick={() => {
                                setIsCreatingNew(false);
                                setNewCompilationName("");
                              }}
                              className="flex-1 py-2.5 rounded-xl bg-gray-700 text-white font-medium hover:bg-gray-600 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleCreateCompilation}
                              disabled={
                                !newCompilationName.trim() || creatingNew
                              }
                              className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              {creatingNew ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                "Save"
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Список компиляций */}
                  <div className="space-y-2">
                    {compilations.map((compilation) => (
                      <div
                        key={compilation.id}
                        className={`flex items-center gap-4 p-3 rounded-2xl cursor-pointer transition-all ${
                          compilation.has_torrent
                            ? "bg-green-500/10 border border-green-500/30"
                            : "hover:bg-white/5"
                        }`}
                        onClick={() => handleToggleCompilation(compilation)}
                      >
                        {/* Preview grid */}
                        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-800 grid grid-cols-2 grid-rows-2 gap-0.5">
                          {compilation.preview_items
                            ?.slice(0, 4)
                            .map((item, idx) => (
                              <div
                                key={item?.id || idx}
                                className="bg-gray-700 overflow-hidden"
                              >
                                {item?.image_url && (
                                  <img
                                    src={item.image_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                            ))}
                          {/* Fill empty slots */}
                          {Array.from({
                            length: Math.max(
                              0,
                              4 - (compilation.preview_items?.length || 0),
                            ),
                          }).map((_, idx) => (
                            <div key={`empty-${idx}`} className="bg-gray-700" />
                          ))}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-semibold text-base truncate">
                            {compilation.title || compilation.name}
                          </h4>
                          <p className="text-gray-400 text-sm">
                            {compilation.is_public ? "Public" : "Private"} •{" "}
                            {compilation.items_count || 0}
                          </p>
                        </div>

                        {/* Action icon */}
                        <div className="flex-shrink-0">
                          {savingCompilation === compilation.id ? (
                            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                          ) : compilation.has_torrent ? (
                            <Check className="w-6 h-6 text-green-500" />
                          ) : (
                            <div className="w-8 h-8 rounded-full border-2 border-gray-600 flex items-center justify-center hover:border-indigo-500 transition-colors">
                              <Plus className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {compilations.length === 0 && !isCreatingNew && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="mb-2">
                          You don't have any collections yet
                        </p>
                        <button
                          onClick={() => setIsCreatingNew(true)}
                          className="text-indigo-400 hover:text-indigo-300 font-medium"
                        >
                          Create first one
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
